import * as yup from "yup";

import i18n from "lib/i18n";
import XRegExp from "xregexp"; // extended regex syntax, so we can match UTF-8 chars

// n.b. this regex _must_ be functionally equivalent to the server-side
// validation regex at lib/forms/validation.rb, because some server-side
// validation happens after payment has already been taken. So, if an email
// passes client-side validation, it must pass server-side validation
const emailRegex = XRegExp("^[\\p{Letter}\\w+\\-.]*[\\p{Letter}\\w+\\-]@[a-z\\d-]+(\\.[a-z\\d-]+)*\\.[a-z]+$", "i");

// Separating out the definitions of validations ("what"s) from the conditions
// which determine whether they run ("when"s).
//
// n.b. we need to pass in `i18n.t()` later rather than just using it now so
// that we can translate the error messages after we know the current language
// has been set
const definitions = translate => ({
  amount: yup.number()
    .required(translate("donation.message.amount_range_error"))
    .typeError(translate("donation.message.amount_range_error"))
    .min(yup.ref("$minAmount"), translate("donation.message.amount_range_error"))
    .max(yup.ref("$maxAmount"), translate("donation.message.amount_range_error")),
  cardNumber: yup.boolean()
    .required(translate("donation.message.credit_card_error"))
    .equals([true], translate("donation.message.credit_card_error")),
  cvv: yup.boolean()
    .required(" ")
    .equals([true], " "),
  // We use a custom regex here instead of yup's built-in email validation because
  // we want our validation to exactly match the one we use server-side.
  // If the client-side validation is looser than the server-side validation,
  // we'll see some email addresses being accepted as valid on the client,
  // payment taken, and then they'll be knocked back when we go to save the
  // donation on the server.
  email: yup.string()
    .required(translate("donation.message.email_error"))
    .matches(emailRegex, translate("donation.message.email_error")),
  name: yup.string()
    .required(translate("donation.message.name_error"))
    .matches(/^[^@]+$/, translate("donation.message.name_error")),
});

export default class Validation {
  constructor(context) {
    this.schemata = Validation.buildSchemata();
    this.context = context;
  }

  /**
   * Build the yup validation schemata
   * https://github.com/jquense/yup
   *
   * Accepts several context variables (eg. $minAmount), the
   * values of which are supplied in the construction of this class.
   */
  static buildSchemata() {
    const { amount, cardNumber, cvv, email, name } = definitions(i18n.t);

    const creditCard = yup.object().shape({ cardNumber, cvv });
    const identity = yup.object().shape({ email, name });
    const amountSchema = yup.object().shape({ amount });
    return {
      credit_card: amountSchema.concat(identity).concat(creditCard),
      paypal: amountSchema.concat(identity),
      wallet: amountSchema,
    };
  }

  /**
   * Validate the state of the donation form
   *
   * Returns a promise that resolves if validation is successful, and is
   * rejected with errors otherwise. Errors are formatted as:
   *   [{field: "message"}]
   */
  validate(state) {
    return new Promise((resolve, reject) => {
      this.schemata[state.paymentProvider || "paypal"].validate(state, { abortEarly: false, context: this.context })
        .then(() => resolve())
        .catch(err => reject(Validation.reformatErrors(err)));
    });
  }

  static reformatErrors(err) {
    return Object.fromEntries(err.inner.map(ex => [ex.path, ex.message]));
  }
}
